import React from 'react';
import styled from 'styled-components';

/**
 * Card/NewsletterWide (Figma componentSet 6460:60778)
 * 690 x 295.71 (21:9), radius 12, 이미지 위 검정 16% 오버레이 + 중앙 정렬 텍스트
 *
 * 호버 시 이미지 레이어만 scale(1.05) — Card/Newsletter 와 동일
 */

interface NewsletterWideCardProps {
  imageUrl: string;
  title: string;
  caption?: string;
  href?: string;
}

const NewsletterWideCard: React.FC<NewsletterWideCardProps> = ({
  imageUrl,
  title,
  caption,
  href,
}) => {
  const handleClick = () => {
    if (!href || href === '#') return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const isClickable = Boolean(href && href !== '#');

  return (
    <Card onClick={handleClick} $clickable={isClickable}>
      <CardImage $imageUrl={imageUrl} />
      <Scrim />
      <Overlay>
        <Container>
          <CardTitle>{title}</CardTitle>
          {caption && <CardCaption>{caption}</CardCaption>}
        </Container>
      </Overlay>
      <CardBorder />
    </Card>
  );
};

export default NewsletterWideCard;

const Card = styled.div<{ $clickable: boolean }>`
  position: relative;
  width: 690px;
  aspect-ratio: 21 / 9;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #f5f5f5;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  @media (max-width: 1280px) {
    width: 100%;
  }

  @media (max-width: 1024px) {
    width: 100%;
    aspect-ratio: 16 / 9;
  }
`;

const CardImage = styled.div<{ $imageUrl: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${({ $imageUrl }) => `url(${$imageUrl})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const Scrim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000000;
  opacity: 0.16;
  pointer-events: none;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

/* Figma: Content 프레임이 alignItems=flex-end, 타이틀 textAlign=RIGHT */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: stretch;
  justify-content: center;
  align-items: flex-end;
  gap: 12px;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 1.36em;
  letter-spacing: -0.0236em;
  white-space: pre-line;
  text-align: right;
  text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08),
    0px 1px 2px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CardCaption = styled.p`
  margin: 0;
  color: #ffffff;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43em;
  letter-spacing: 0.0145em;
  text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08),
    0px 1px 2px rgba(0, 0, 0, 0.12);
`;

const CardBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px solid rgba(112, 115, 124, 0.08);
  border-radius: 12px;
  pointer-events: none;
`;
